import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LoadingScreen from 'src/Loading/LoadingScreen';
import { getUserInfo, getUserRolePermissions } from '../services/userServicer';
import { getV2Token } from '../utils/v2AuthUtils';
import { isSessionFailure, redirectToLogin } from '../utils/sessionExpiry';
import Forbidden from './Forbidden';
import { isDemoUser } from '../utils/demoAuth';
import Dashboard from '../pages/dashboard/Dashboard';
import DemoDashboard from '../pages/demo/DemoDashboard';

interface RoleBasedRouteProps {
  children?: React.ReactNode;
}

const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({ children }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(false);
  const [notAllowed, setNotAllowed] = useState(false);

  useEffect(() => {
    const token = getV2Token();
    if (token && isDemoUser(token)) {
      setIsDemo(true);
      setIsLoading(false);
      return;
    }

    const checkDashboardAccess = async () => {
      // Keeps the loader up while we navigate to /login, instead of flashing the
      // dashboard (and firing another round of doomed calls) on the way out.
      let leaving = false;
      try {
        setIsLoading(true);
        const userData = await getUserInfo();
        const userId = userData?.id;
        if (!userId) {
          setNotAllowed(true);
          return;
        }

        // Permission gate for dashboard (root "/")
        const permsResp = await getUserRolePermissions(userId);

        // An expired/replaced session (401) or a rejected CSRF token is NOT a permission
        // answer. Rendering "not allowed" for it stranded the user inside a shell where
        // every call failed; send them back to log in instead.
        if (isSessionFailure(permsResp)) {
          leaving = true;
          await redirectToLogin();
          return;
        }

        // The landing route renders the Compliance Dashboard (see the <Dashboard /> below),
        // so it gates on the "cs Dashboard" row — the same page that screen checks for
        // itself, which keeps the route and its content from disagreeing. NOT "Dashboard",
        // which is a different page (analytics), and no longer "Home".
        const perms: any[] = permsResp?.success ? permsResp?.data?.permissions || [] : [];
        const findPage = (name: string) =>
          perms.find((p: any) => String(p?.page || '').trim().toLowerCase() === name);
        const dashboardPerm = findPage('cs dashboard');

        if (!dashboardPerm?.show) {
          setNotAllowed(true);
          return;
        }

        setNotAllowed(false);
      } catch (err) {
        console.error('Error checking dashboard access:', err);
        setNotAllowed(true);
      } finally {
        if (!leaving) setIsLoading(false);
      }
    };

    checkDashboardAccess();
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isDemo) {
    return <DemoDashboard />;
  }

  if (notAllowed) {
    // 403, not 401: getUserInfo() succeeded above, so the session is valid and it is the
    // group's 'cs Dashboard' permission row that says no. Signing in again would change nothing.
    return <Forbidden page={t('Sidebar.Dashboard') || 'Dashboard'} />;
  }

  // If children are provided, render them (for custom role-based components)
  if (children) {
    return <>{children}</>;
  }

  // All roles/tenants land on the Compliance Dashboard (screenshot dashboard).
  return <Dashboard />;
};

export default RoleBasedRoute;
